import { ROUTES } from "../components/shared/constant";
import { IRouteDetails } from "../common/App.interface";
import { EMPLOYEE_BASE_PATH, EMPLOYEE_USER_VIEW_PATH } from "./employeePaths";
import {
  EmployeeHomePage,
  EmployeeManageContentPage,
  EmployeeAnswerSurveyPage,
  EmployeeManageAcksPage,
  EmployeeManageBroadcastsPage,
  EmployeeBroadcastComposerPage,
  EmployeeBroadcastReportPage,
  EmployeeManageRewardsPage,
  EmployeeManageSurveysPage,
  EmployeeRecognitionAdminPage,
  EmployeeGiftcardsAdminPage,
  EmployeeLibraryAdminPage,
  EmployeeMessageReportsRedirect,
  EmployeeReportsPage,
  EmployeeManageScopePage,
  EmployeeMyDirectoryPage,
  EmployeeMyHomePage,
  EmployeeMyPostPage,
  EmployeeMyMessagesPage,
  EmployeeMyProfilePage,
  EmployeeMyResourcesPage,
  EmployeeMyRewardsPage,
  EmployeeLeaderboardPage,
  EmployeeMySurveysPage,
  EmployeeMyRequestsPage,
  EmployeeNewRequestPage,
  EmployeeNotificationSettingsPage,
  EmployeeRequestDetailPage,
  EmployeeRequestHelpPage,
  EmployeeRequestPortalPage,
  EmployeeUsersAndOrgPage,
  EmployeeSettingsPage,
  EmployeeMessagingSettingsPage,
} from "./employeeLazyComponents";

/**
 * Route table for the skyEmployee module. Each feature page is one entry here
 * plus one in employeeNavConfig.ts.
 */
export function createEmployeeRoutes(): IRouteDetails[] {
  return [
    {
      name: "EmployeeHome",
      path: ROUTES.EMPLOYEE_HOME + ROUTES.QUERYPARAMS,
      component: EmployeeHomePage,
      isRealTimeDataOn: false,
    },
    {
      // Admin-only; the page itself guards with RequireCapability.
      name: "EmployeeUsersAndOrg",
      path: ROUTES.EMPLOYEE_ADMIN_USERS + ROUTES.QUERYPARAMS,
      component: EmployeeUsersAndOrgPage,
      isRealTimeDataOn: false,
    },
    {
      // Admin-only (settings.manage); the page itself guards with RequireCapability.
      name: "EmployeeSettings",
      path: ROUTES.EMPLOYEE_ADMIN_SETTINGS + ROUTES.QUERYPARAMS,
      component: EmployeeSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      // Admin-only (settings.manage); the page itself guards with RequireCapability.
      name: "EmployeeMessagingSettings",
      path: ROUTES.EMPLOYEE_ADMIN_MESSAGING + ROUTES.QUERYPARAMS,
      component: EmployeeMessagingSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      // Admin-only (recognition.admin); the page itself guards with RequireCapability.
      name: "EmployeeRecognitionAdmin",
      path: ROUTES.EMPLOYEE_ADMIN_RECOGNITION + ROUTES.QUERYPARAMS,
      component: EmployeeRecognitionAdminPage,
      isRealTimeDataOn: false,
    },
    {
      // Admin-only (giftcards.admin); the page itself guards with RequireCapability.
      name: "EmployeeGiftcardsAdmin",
      path: ROUTES.EMPLOYEE_ADMIN_GIFTCARDS + ROUTES.QUERYPARAMS,
      component: EmployeeGiftcardsAdminPage,
      isRealTimeDataOn: false,
    },
    {
      // Admin-only (library.manage); the page itself guards with RequireCapability.
      name: "EmployeeLibraryAdmin",
      path: ROUTES.EMPLOYEE_ADMIN_LIBRARY + ROUTES.QUERYPARAMS,
      component: EmployeeLibraryAdminPage,
      isRealTimeDataOn: false,
    },
    {
      // Content moderators and Admins; each tab guards its own capability.
      name: "EmployeeReports",
      path: ROUTES.EMPLOYEE_MANAGE_REPORTS + ROUTES.QUERYPARAMS,
      component: EmployeeReportsPage,
      isRealTimeDataOn: false,
    },
    {
      // The old Reported messages address, for bookmarks and earlier links.
      name: "EmployeeMessageReports",
      path: ROUTES.EMPLOYEE_ADMIN_MESSAGE_REPORTS + ROUTES.QUERYPARAMS,
      component: EmployeeMessageReportsRedirect,
      isRealTimeDataOn: false,
    },
    {
      // Managers only; the page itself guards with RequireCapability.
      name: "EmployeeManageScope",
      path: ROUTES.EMPLOYEE_MANAGE_SCOPE + ROUTES.QUERYPARAMS,
      component: EmployeeManageScopePage,
      isRealTimeDataOn: false,
    },
    {
      // Managers and Admins; each tab guards its own capability.
      name: "EmployeeManageContent",
      path: ROUTES.EMPLOYEE_MANAGE_CONTENT + ROUTES.QUERYPARAMS,
      component: EmployeeManageContentPage,
      isRealTimeDataOn: false,
    },
    {
      // Managers and Admins (giftcards.award); the page itself guards with RequireCapability.
      name: "EmployeeManageRewards",
      path: ROUTES.EMPLOYEE_MANAGE_REWARDS + ROUTES.QUERYPARAMS,
      component: EmployeeManageRewardsPage,
      isRealTimeDataOn: false,
    },
    {
      // Managers and Admins (acks.track); the page itself guards with RequireCapability.
      // Writing a broadcast; the page checks the send permission itself.
      name: "EmployeeBroadcastComposer",
      path: ROUTES.EMPLOYEE_MANAGE_BROADCAST_COMPOSE + ROUTES.QUERYPARAMS,
      component: EmployeeBroadcastComposerPage,
      isRealTimeDataOn: false,
    },
    {
      // A broadcast's report; the page checks the permissions itself.
      name: "EmployeeBroadcastReport",
      path: ROUTES.EMPLOYEE_MANAGE_BROADCAST_REPORT + ROUTES.QUERYPARAMS,
      component: EmployeeBroadcastReportPage,
      isRealTimeDataOn: false,
    },
    {
      // Broadcasts: the page shows only what Who can do what allows.
      name: "EmployeeManageBroadcasts",
      path: ROUTES.EMPLOYEE_MANAGE_BROADCASTS + ROUTES.QUERYPARAMS,
      component: EmployeeManageBroadcastsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "EmployeeManageAcks",
      path: ROUTES.EMPLOYEE_MANAGE_ACKS + ROUTES.QUERYPARAMS,
      component: EmployeeManageAcksPage,
      isRealTimeDataOn: false,
    },
    {
      // Answering a survey: the employee view, reached from the due list.
      name: "EmployeeAnswerSurvey",
      path: ROUTES.EMPLOYEE_MY_SURVEY + ROUTES.QUERYPARAMS,
      component: EmployeeAnswerSurveyPage,
      isRealTimeDataOn: false,
    },
    {
      // Managers and Admins; each tab guards its own capability.
      name: "EmployeeManageSurveys",
      path: ROUTES.EMPLOYEE_MANAGE_SURVEYS + ROUTES.QUERYPARAMS,
      component: EmployeeManageSurveysPage,
      isRealTimeDataOn: false,
    },
    {
      // Header → View profile from the admin view; open to everyone in skyEmployee.
      name: "EmployeeProfile",
      path: ROUTES.EMPLOYEE_PROFILE + ROUTES.QUERYPARAMS,
      component: EmployeeMyProfilePage,
      isRealTimeDataOn: false,
    },
    {
      // The bell's Notification settings from the admin view; open to everyone in skyEmployee.
      name: "EmployeeAdminNotificationSettings",
      path: ROUTES.EMPLOYEE_NOTIFICATION_SETTINGS + ROUTES.QUERYPARAMS,
      component: EmployeeNotificationSettingsPage,
      isRealTimeDataOn: false,
    },
    // Employee-facing view (Portal View → User Portal). Open to everyone in skyEmployee.
    ...(
      [
        ["EmployeeMyPost", ROUTES.EMPLOYEE_MY_POST, EmployeeMyPostPage],
        ["EmployeeMyHome", ROUTES.EMPLOYEE_MY_HOME, EmployeeMyHomePage],
        ["EmployeeMyRequests", ROUTES.EMPLOYEE_MY_REQUESTS, EmployeeMyRequestsPage],
        ["EmployeeRequestDetail", ROUTES.EMPLOYEE_MY_REQUEST_DETAIL, EmployeeRequestDetailPage],
        // Most specific first: every path ends in an optional /:instanceid, so
        // e.g. /new-request/EMP also matches /new-request/:instanceid with the
        // same score, and the first listed wins (Service Desk orders its
        // customer routes the same way).
        ["EmployeeRequestArticle", ROUTES.EMPLOYEE_MY_NEW_REQUEST_ARTICLE, EmployeeRequestHelpPage],
        ["EmployeeRequestHelp", ROUTES.EMPLOYEE_MY_NEW_REQUEST_HELP, EmployeeRequestHelpPage],
        ["EmployeeRequestPortal", ROUTES.EMPLOYEE_MY_NEW_REQUEST_PORTAL, EmployeeRequestPortalPage],
        ["EmployeeNewRequest", ROUTES.EMPLOYEE_MY_NEW_REQUEST, EmployeeNewRequestPage],
        ["EmployeeMyMessages", ROUTES.EMPLOYEE_MY_MESSAGES, EmployeeMyMessagesPage],
        ["EmployeeMyDirectory", ROUTES.EMPLOYEE_MY_DIRECTORY, EmployeeMyDirectoryPage],
        ["EmployeeMyRewards", ROUTES.EMPLOYEE_MY_REWARDS, EmployeeMyRewardsPage],
        ["EmployeeLeaderboard", ROUTES.EMPLOYEE_MY_LEADERBOARD, EmployeeLeaderboardPage],
        ["EmployeeMySurveys", ROUTES.EMPLOYEE_MY_SURVEYS, EmployeeMySurveysPage],
        ["EmployeeMyResources", ROUTES.EMPLOYEE_MY_RESOURCES, EmployeeMyResourcesPage],
        ["EmployeeMyProfile", ROUTES.EMPLOYEE_MY_PROFILE, EmployeeMyProfilePage],
        [
          "EmployeeNotificationSettings",
          ROUTES.EMPLOYEE_MY_NOTIFICATION_SETTINGS,
          EmployeeNotificationSettingsPage,
        ],
      ] as const
    ).map(([name, path, component]) => ({
      name,
      path: path + ROUTES.QUERYPARAMS,
      component,
      isRealTimeDataOn: false as const,
    })),
    // The same pages in the admin view (the address without "/my"), so Admins
    // and Managers see what people see, with their own controls, without
    // switching views. Home, the profile and notification settings have their
    // admin-view routes above.
    ...ADMIN_VIEW_SHARED_PAGES.map(([name, path, component]) => ({
      name: `${name}AdminView`,
      path: path.replace(EMPLOYEE_USER_VIEW_PATH, EMPLOYEE_BASE_PATH) + ROUTES.QUERYPARAMS,
      component,
      isRealTimeDataOn: false as const,
    })),
  ];
}

/** Employee-view pages that also open in the admin view, as [name, employee-view path, page]. */
const ADMIN_VIEW_SHARED_PAGES = [
  ["EmployeeMyPost", ROUTES.EMPLOYEE_MY_POST, EmployeeMyPostPage],
  ["EmployeeMyRequests", ROUTES.EMPLOYEE_MY_REQUESTS, EmployeeMyRequestsPage],
  ["EmployeeRequestDetail", ROUTES.EMPLOYEE_MY_REQUEST_DETAIL, EmployeeRequestDetailPage],
  // Most specific first, as in the employee view.
  ["EmployeeRequestArticle", ROUTES.EMPLOYEE_MY_NEW_REQUEST_ARTICLE, EmployeeRequestHelpPage],
  ["EmployeeRequestHelp", ROUTES.EMPLOYEE_MY_NEW_REQUEST_HELP, EmployeeRequestHelpPage],
  ["EmployeeRequestPortal", ROUTES.EMPLOYEE_MY_NEW_REQUEST_PORTAL, EmployeeRequestPortalPage],
  ["EmployeeNewRequest", ROUTES.EMPLOYEE_MY_NEW_REQUEST, EmployeeNewRequestPage],
  ["EmployeeMyMessages", ROUTES.EMPLOYEE_MY_MESSAGES, EmployeeMyMessagesPage],
  ["EmployeeMyDirectory", ROUTES.EMPLOYEE_MY_DIRECTORY, EmployeeMyDirectoryPage],
  ["EmployeeMyRewards", ROUTES.EMPLOYEE_MY_REWARDS, EmployeeMyRewardsPage],
  ["EmployeeLeaderboard", ROUTES.EMPLOYEE_MY_LEADERBOARD, EmployeeLeaderboardPage],
  ["EmployeeMyResources", ROUTES.EMPLOYEE_MY_RESOURCES, EmployeeMyResourcesPage],
  // Answering one before the list: every path ends in an optional
  // /:instanceid, so the list's /surveys/:instanceid? would also match
  // /surveys/<runId>, and on a tie the route listed first wins.
  ["EmployeeAnswerSurvey", ROUTES.EMPLOYEE_MY_SURVEY, EmployeeAnswerSurveyPage],
  ["EmployeeMySurveys", ROUTES.EMPLOYEE_MY_SURVEYS, EmployeeMySurveysPage],
] as const;
